import React, { ReactNode, useContext, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { LoadingMask } from ".";
import { ActionType, BeneficiaryAndApiHeaders } from "../@types";
import { getLoggedInBeneInfo } from "../api";
import routes from "../app/routes";
import { AppContext } from "../app/store";
import {
  beneKeysMatch,
  useTranslate,
  logout,
  getIsSlsxAuthenticated,
  hasLocalSession,
  useBeneLoginAndUpdateHandler,
  useLoggedIn,
} from "../helpers";
import { ApiResponseError } from "../helpers/errors";
import { useHandleMbpLandingRoute } from "../helpers/routeHelpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { erase } from "browser-cookies";
import { useSetLangFromRelayParam } from "../helpers/languageHelpers";

// @TODO - Re-evaluate in MCT-7039
// Taken from now-defunct `UserActivityMonitor`
const doNotRetrieveMissingBeneRoutes: string[] = [
  routes.csrLanding,
  routes.mbpLandingPage,
  routes.slsCallback,
];

export const GlobalSessionHandler = ({ children }: { children: ReactNode }) => {
  // * Hooks
  const history = useHistory();
  const t = useTranslate();
  const beneLoginAndUpdateHandler = useBeneLoginAndUpdateHandler();
  const handleMbpLandingRoute = useHandleMbpLandingRoute();
  const isLoggedIn = useLoggedIn();

  // * Context
  const {
    state: { beneficiary: stateBene, mbpHandoffFailed, csr },
    dispatch,
  } = useContext(AppContext);

  // * Location
  const location = useLocation();
  const isLoginCallbackRoute = location.pathname === routes.slsCallback;
  const isMbpLandingRoute = location.pathname === routes.mbpLandingPage;
  const isCsr = csr || location.pathname === routes.csrLanding;

  // * Flags
  const flags = useFlags();
  const { feEnableAuthentication } = flags as {
    feEnableAuthentication: boolean;
  };

  // * State
  const [loading, setLoading] = useState(
    // Start with loading state set to true for callback routes
    // Callback routes always use the `redirect_uri` to send the user somewhere else
    isLoginCallbackRoute || isMbpLandingRoute
  );
  /**
   * local copy of endpoint result
   * ? could potentially be removed by changing how we deal with queries
   * ? for example using `useQuery` library. */
  const [beneInfoWithHeaders, setBeneInfoWithHeaders] =
    useState<BeneficiaryAndApiHeaders>();

  // * Constants
  const hasStateBene = !!stateBene;
  const fetchedBene = beneInfoWithHeaders?.beneficiary;
  const skipHandling = !feEnableAuthentication || !!isCsr;

  // * Effects

  useSetLangFromRelayParam();

  useEffect(() => {
    if (skipHandling) {
      setLoading(false);
    }
  }, [skipHandling]);

  useEffect(() => {
    console.debug(`🐝 Inside GlobalSessionHandler, logged in? ${isLoggedIn}`);
  }, [isLoggedIn]);

  /**
   * If we are authenticated and have not _already_ fetched beneInfo,
   * we call `getLoggedInBeneInfo()`
   */
  useEffect(() => {
    (async () => {
      let isMounted = true;
      function dismount() {
        isMounted = false;
      }
      if (skipHandling || beneInfoWithHeaders || isMbpLandingRoute) {
        return dismount;
      }
      try {
        // Check whether the user is authenticated. If not, throwing here will
        // let the user continue and actively logout if state needs clearing
        if (!isLoginCallbackRoute && !(await getIsSlsxAuthenticated())) {
          throw new Error("No global session or error getting global session");
        }
        // @TODO - All non-CSR routes now call `getLoggedInBeneInfo` (`/beneinfo/userinfo`)
        // which needs no values from a `req_token` query param.
        // Try to unify the handling of all auth routes, as much as possible
        //
        // Get latest beneInfo
        const result = await getLoggedInBeneInfo();
        if (isMounted) {
          setBeneInfoWithHeaders(result);
        }
      } catch (e) {
        console.debug(`🦄 Could not fetch a bene in GlobalSessionHandler`);
        let apiResponseStatus;
        if (e instanceof ApiResponseError) {
          const { info } = e;
          apiResponseStatus = info.apiResponseStatus;
        }
        // If app state (store) still holds bene info, we need to actively log
        // out, locally, clear everything out, and return to non-LI landing
        if (hasStateBene) {
          console.debug(`🦞 Actively logging out`);
          logout({
            dispatch,
            apiExpired: apiResponseStatus === 401,
            clientExpired: true,
          });
        }
        if (hasLocalSession()) {
          // expire `sid` cookie, if it's still hanging around
          erase("sid");
        }
        // This likely means there is no session, and the page should proceed to
        // load unauthenticated
        // @TODO - May need to add conditional toast/routing here, even if there
        // is no bene in app state
        console.debug(`🐠 Continuing unauthenticated`);
        setLoading(false);
      }

      return dismount;
    })();
  }, [
    beneInfoWithHeaders,
    dispatch,
    hasStateBene,
    isLoginCallbackRoute,
    isMbpLandingRoute,
    skipHandling,
  ]);

  useEffect(() => {
    (async () => {
      if (skipHandling) return;
      // @TODO - MBP Landing page should be handled similarly to login callback route,
      // and should share logic, as much as possible
      if (isMbpLandingRoute) {
        console.debug(
          `🐪 MBP landing route. mbpHandoffFailed? ${mbpHandoffFailed}`
        );
        dispatch({
          type: ActionType.RESET_STATE,
        });
        return handleMbpLandingRoute().then(
          renderChildren => renderChildren && setLoading(false)
        );
      }

      if (!beneInfoWithHeaders) return;

      // For non-login page loads, if there's bene info already in app state,
      // and the cached bene and fetched bene are the same, just update bene info and return
      if (
        !isLoginCallbackRoute &&
        stateBene &&
        fetchedBene &&
        beneKeysMatch(stateBene, fetchedBene)
      ) {
        console.debug(`😺 updated the state with fetched bene info`);

        dispatch({
          type: ActionType.ADD_BENEFICIARY,
          payload: fetchedBene,
        });

        console.debug(
          `🌸 LI bene page reload, returning from GlobalSessionHandler, continuing authenticated`
        );
        return;
      }

      // @TODO - Re-evaluate in MCT-7039 (https://jira.cms.gov/browse/MCT-7039)
      // Taken from now-defunct `UserActivityMonitor`
      // Pushes a location to history for non-callback routes
      // May be used in the `beneLoginAndUpdateHandler`
      // * See MCT-9106 as well (https://jira.cms.gov/browse/MCT-9106)
      const doNotSetHistoryStateFromRoute =
        doNotRetrieveMissingBeneRoutes.includes(location.pathname);
      if (hasLocalSession() && !stateBene && !doNotSetHistoryStateFromRoute) {
        const historyStateFromRoute = {
          pathname: `${routes.slsCallback}`,
          state: { from: `${location.pathname}${location.search}` },
        };
        history.push(historyStateFromRoute);
      }

      // At this point, the user has a session, but we may need to fully update
      // the store for a LI bene experience. This logic applies to the
      // `loginCallbackRoute` and potentially other routes, if coming to MCT for
      // the first time from an authenticated MBP session
      setLoading(true);
      await beneLoginAndUpdateHandler(beneInfoWithHeaders);
      setLoading(false);
    })();
    // TODO: Break this useEffect into multiple parts, if possible.
    // TODO: MCT-9100 - Update GlobalSessionHandler dependency arrays (https://jira.cms.gov/browse/MCT-9100)
  }, [
    isLoginCallbackRoute,
    isMbpLandingRoute,
    skipHandling,
    beneInfoWithHeaders,
  ]);

  return loading ? (
    <LoadingMask
      loading={true}
      message={t("loading.retrieving_your_information")}
    />
  ) : (
    <>{children}</>
  );
};
